import { useMediaUpload } from "@shared/hooks/useMediaUpload";
import { MediaFile, MediaType } from "@shared/types/media.types";
import { formatImageTag } from "@shared/utils/formatters";
import { useRef, useState } from "react";

interface ImageUploadButtonsProps {
  /**
   * Callback function that will be called when an image is successfully uploaded
   */
  onImageUploaded: (imageUrl: string) => void;

  /**
   * Custom class name for the container div
   */
  className?: string;

  /**
   * Custom text for the "Add Image" button
   */
  addImageText?: string;

  /**
   * Custom text for the "Paste Image" button
   */
  pasteImageText?: string;

  /**
   * Custom text for the help text
   */
  helpText?: string;

  /**
   * Custom options for the media upload
   */
  uploadOptions?: {
    testId?: string;
  };

  /**
   * Custom format for the image tag. Use {url} as a placeholder for the image URL.
   * Default: '[img src="{url}"]'
   */
  imageTagFormat?: string;

  /**
   * If true, the component will automatically format the image URL using the imageTagFormat
   * and pass the formatted tag to onImageUploaded. If false, it will pass the raw URL.
   * Default: false
   */
  autoFormatImageTag?: boolean;
}

/**
 * A reusable component that provides buttons for uploading images via file selection or clipboard paste
 */
export const ImageUploadButtons = ({
  onImageUploaded,
  className = "",
  addImageText = "Add Image",
  pasteImageText = "Paste Image",
  helpText = "Add images for graphs, formulas, etc.",
  uploadOptions = {},
  imageTagFormat = '[img src="{url}"]',
  autoFormatImageTag = true,
}: ImageUploadButtonsProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadMedia } = useMediaUpload(uploadOptions);

  /**
   * Get image from clipboard and process it
   * @returns A Promise that resolves to a MediaFile object if an image is found, or null if no image is found
   */
  const getImageFromClipboard = async (): Promise<MediaFile | null> => {
    try {
      // Try to read clipboard data
      const items = await navigator.clipboard.read();

      // Find image items in clipboard
      for (const item of items) {
        for (const type of item.types) {
          // Check if the item is an image
          if (type.startsWith("image/")) {
            // Get the image blob
            const blob = await item.getType(type);

            // Create a file from the blob
            const fileName = `pasted-image-${new Date().getTime()}.${
              type.split("/")[1] || "png"
            }`;
            const file = new File([blob], fileName, { type });

            // Create MediaFile object
            const mediaFile: MediaFile = {
              file,
              preview: URL.createObjectURL(blob),
              type: type.startsWith("image/")
                ? MediaType.IMAGE
                : MediaType.VIDEO,
              progress: 0,
            };

            return mediaFile;
          }
        }
      }

      // No image found
      return null;
    } catch (error) {
      console.error("Error accessing clipboard:", error);
      throw error;
    }
  };

  /**
   * Handle pasting image from clipboard
   */
  const handlePasteImage = async () => {
    try {
      setIsUploading(true);
      setUploadProgress(10);

      // Get image from clipboard
      const mediaFile = await getImageFromClipboard();

      // Check if an image was found
      if (!mediaFile) {
        alert(
          "No image found in clipboard. Copy an image first and try again."
        );
        return;
      }

      // Upload image to Firebase Storage using MediaService
      const imageUrl = await uploadMedia(mediaFile);
      setUploadProgress(100);

      // Format the image URL if autoFormatImageTag is true, otherwise pass the raw URL
      const result = autoFormatImageTag
        ? formatImageTag(imageUrl, imageTagFormat)
        : imageUrl;
      onImageUploaded(result);
    } catch (error) {
      console.error("Error pasting image from clipboard:", error);
      alert("Error pasting image from clipboard. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Handle file selection for image upload
   */
  const handleFileImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadProgress(10);

      // Create MediaFile object
      const mediaFile: MediaFile = {
        file,
        preview: URL.createObjectURL(file),
        type: file.type.startsWith("image/")
          ? MediaType.IMAGE
          : MediaType.VIDEO,
        progress: 0,
      };

      // Upload image to Firebase Storage using MediaService
      const imageUrl = await uploadMedia(mediaFile);
      setUploadProgress(100);

      // Format the image URL if autoFormatImageTag is true, otherwise pass the raw URL
      const result = autoFormatImageTag
        ? formatImageTag(imageUrl, imageTagFormat)
        : imageUrl;
      onImageUploaded(result);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Error uploading image. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex space-x-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={`px-3 py-1 text-sm ${
            isUploading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-500 hover:bg-green-600"
          } text-white rounded-md flex items-center`}
          type="button"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            ></path>
          </svg>
          {isUploading ? `Uploading ${uploadProgress}%` : addImageText}
        </button>
        <button
          onClick={handlePasteImage}
          disabled={isUploading}
          className={`px-3 py-1 text-sm ${
            isUploading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600"
          } text-white rounded-md flex items-center`}
          title="Paste image from clipboard"
          type="button"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          {pasteImageText}
        </button>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileImageUpload}
        className="hidden"
        accept="image/*"
      />
      {helpText && (
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
          {helpText}
        </span>
      )}
    </div>
  );
};
